import * as Sentry from '@sentry/vue'
import { AxiosError } from 'axios'
import { BootstrapVue, IconsPlugin } from 'bootstrap-vue'
import Bowser from 'bowser'
import CountriesAndTimezones from 'countries-and-timezones'
import googlePhone from 'google-libphonenumber'
import 'highlight.js/styles/github.css'
import momentDurationFormatSetup from 'moment-duration-format'
import moment from 'moment-timezone'
import PortalVue from 'portal-vue'
import { Screen } from 'quasar'
import { NOTIFICATION_CONFIGURATION } from 'src/constants/bootstrap-default'
import { cloneDeep } from 'src/plugins/helpers/functions'
import { errorNotificationMessage } from 'src/plugins/helpers/error-notification-message'
import { drainReminderNotificationQueue, enqueueReminderNotification } from 'src/plugins/helpers/reminder-notification-queue'
import {
  getLocaleIfPhoneNumberIsFromGreatBritainOrAustralia,
  getLocaleIfPhoneNumberIsFromNorthAmerica,
  guessLocale
} from 'src/plugins/helpers/phone-locale'
import { isBrowserExtensionError, isInjectedPageScriptError, isIntercomWidgetError } from 'src/plugins/helpers/sentry-event-filters'
import { createSentryIdentitySubscriber } from 'src/plugins/helpers/sentry-identity'
import * as storage from 'src/plugins/helpers/storage'
import Vue from 'vue'
import BusinessHours from 'vue-business-hours'
import infiniteScroll from 'vue-infinite-scroll'
import 'vue-popperjs/dist/vue-popper.css'
import VueWaveSurfer from 'components/vue-wave-surfer.vue'
import { Vuelidate } from 'vuelidate'

Screen.setSizes({
  sm: 300,
  md: 605,
  lg: 1000,
  xl: 2000
})

// local storage
storage.local.setItem('api_url', process.env.API_URL)
storage.local.setItem('api_reporting_url', process.env.API_REPORTING_URL)

storage.local.setItem('sentry_dsn_public', process.env.VITE_SENTRY_DSN_PUBLIC)
storage.local.setItem('aloware_demo_companies', Object.values(process.env.DEMO_COMPANY_IDS).join(','))

Vue.use(infiniteScroll)
Vue.use(BootstrapVue)
Vue.use(IconsPlugin)
Vue.use(PortalVue)
Vue.component('vue-wave-surfer', VueWaveSurfer)
Vue.use(Vuelidate)
Vue.use(BusinessHours)

window.Bowser = Bowser
window.timezone = 'Intl' in window
  ? new Intl.DateTimeFormat().resolvedOptions().timeZone
  : 'America/Los_Angeles'

if (process.env.APP_DEBUG) {
  Vue.config.devtools = true
}

// Require `PhoneNumberFormat`.
window.PNF = googlePhone.PhoneNumberFormat

// Get an instance of `PhoneNumberUtil`.
window.phoneUtil = googlePhone.PhoneNumberUtil.getInstance()

// Timezones for international companies (outside US and CA)
window.CountriesAndTimezones = CountriesAndTimezones

// Locale guessing lives in a plain module so it is unit-testable without booting
// Sentry and the rest of this file. Still assigned onto window so the
// global surface is unchanged: the fixPhone filter calls window.guessLocale, and
// the two narrower helpers have no callers today but were already exposed.
window.getLocaleIfPhoneNumberIsFromNorthAmerica = getLocaleIfPhoneNumberIsFromNorthAmerica
window.getLocaleIfPhoneNumberIsFromGreatBritainOrAustralia = getLocaleIfPhoneNumberIsFromGreatBritainOrAustralia
window.guessLocale = guessLocale

window._ = require('lodash')
// Import the pusher-js library for WebSocket functionality (live updates)
window.Pusher = require('pusher-js')

window.paceOptions = {
  restartOnRequestAfter: false,
  ajax: {
    trackWebSockets: false
  }
}

momentDurationFormatSetup(moment)
moment.locale('en')
window.moment = moment

window.Sentry = Sentry

// set env to browser's local storage
if (process.env.APP_ENV === 'production') {
  storage.local.setItem('env', 'production')
}

if (process.env.APP_ENV !== 'production') {
  storage.local.setItem('env', 'development')
}

const isNotLocal = process.env.NODE_ENV === 'production' || process.env.NODE_ENV === 'development'

if (isNotLocal && process.env.APP_ENV !== 'local') {
  Sentry.init({
    Vue,
    dsn: process.env.SENTRY_DSN_PUBLIC,
    environment: process.env.APP_ENV,
    release: process.env.VERSION,
    tracingOptions: {
      trackComponents: true
    },
    attachProps: true,
    trackComponents: true,
    tracesSampleRate: process.env.SENTRY_TRACES_SAMPLE_RATE,
    ignoreErrors: [
      'Non-Error promise rejection captured with value: undefined',
      'Cannot read property \'is_reseller\' of null',
      'Cannot set property \'_height\' of undefined',
      'Cannot read property \'offsetHeight\' of undefined',
      'No error message',
      '$ is not defined',
      'ResizeObserver loop limit exceeded',
      'ResizeObserver loop completed with undelivered notifications.',
      'Request failed with status code 504',
      'Request failed with status code 500',
      'Action timed out',
      'Network Error',
      'Expired token',
      'Network request failed',
      'Failed to fetch',
      'NetworkError',
      'Navigation cancelled from',
      'Blocked a frame with origin',
      'AxiosError: Request failed with status code 401',
      'AxiosError: Request failed with status code 404',
      'AxiosError: Request failed with status code 422',
      'AxiosError: Request aborted',
      'AxiosError: timeout exceeded',
      /^CanceledError:/
    ],

    replaysSessionSampleRate: 0,

    // If the entire session is not sampled, use the below sample rate to sample
    // sessions when an error occurs.
    replaysOnErrorSampleRate: 1.0,

    integrations: [
      Sentry.browserTracingIntegration()
    ],

    beforeSend (event, hint) {
      if (hint?.originalException instanceof AxiosError && hint.originalException?.response) {
        if (hint.originalException.response?.status === 404) {
          return null
        }
      }

      if (isInjectedPageScriptError(event) || isIntercomWidgetError(event) || isBrowserExtensionError(event)) {
        return null
      }

      return event
    }
  })

  Sentry.setTag('environment', process.env.NODE_ENV)
  // Same tag name the backend sets from the Version header, so events are
  // filterable by frontend_version across the talk2 and api-core projects.
  Sentry.setTag('frontend_version', process.env.VERSION)
}

// Branding :D
console.log(
  `%c
 █████╗ ██╗      ██████╗ ██╗    ██╗ █████╗ ██████╗ ███████╗
██╔══██╗██║     ██╔═══██╗██║    ██║██╔══██╗██╔══██╗██╔════╝
███████║██║     ██║   ██║██║ █╗ ██║███████║██████╔╝█████╗
██╔══██║██║     ██║   ██║██║███╗██║██╔══██║██╔══██╗██╔══╝
██║  ██║███████╗╚██████╔╝╚███╔███╔╝██║  ██║██║  ██║███████╗
╚═╝  ╚═╝╚══════╝ ╚═════╝  ╚══╝╚══╝ ╚═╝  ╚═╝╚═╝  ╚═╝╚══════╝
`,
  'color: #00BF50'
)

Vue.prototype.$moment = window.moment
// Keep the Pusher reference for compatibility, but used with Soketi (live updates)
Vue.prototype.$Pusher = window.Pusher
Vue.prototype.$Sentry = window.Sentry

Vue.prototype.$handleErrors = function (response, title = null) {
  if (response && response.status) {
    const message = errorNotificationMessage(response)

    if (window._.isEmpty(message.trim())) {
      return
    }

    this.$generalNotification(message, 'error', 5000, true)
  }
}

Vue.prototype.$handleUploadErrors = function (error) {
  if (typeof error === 'string') {
    error = JSON.parse(error)
  }

  let err = {}

  if (error.message === 'This action is unauthorized.') {
    err = {
      status: 403
    }
  } else {
    err = {
      status: 422,
      data: {
        errors: error.errors.file
      }
    }
  }

  this.$handleErrors(err)
}

Vue.prototype.$handleRouteError = (error) => {
  if (error.name !== 'NavigationDuplicated' &&
    !error.message.includes('Avoided redundant navigation to current location')) {
    console.log(error)
  }
}

Vue.prototype.$downloadFileWithUuid = async function (uuid, filename, type = 'common') {
  if (!uuid) {
    this.$generalNotification('Failed to download file, missing UUID', 'error')
  }

  const url = (type !== 'csv'
    ? '/static/uploaded_file/'
    : '/download/') + uuid

  return window.axios.get(url, {
    params: {
      force_download: 1
    },
    responseType: 'arraybuffer'
  })
    .then((response) => {
      const url = window.URL.createObjectURL(new Blob([response.data]))
      const link = document.createElement('a')
      link.href = url
      link.setAttribute('download', filename)
      link.click()
    })
    .catch(() => {
      this.$generalNotification('Failed to download file.', 'error')
    })
}

Vue.prototype.$downloadFileWithUrl = async function (url, filename, type = 'common') {
  if (!url) {
    this.$generalNotification('Failed to download file, missing URL', 'error')
  }

  return fetch(url)
    .then(async (response) => {
      const link = document.createElement('a')
      const file = await response.blob()
      link.href = window.URL.createObjectURL(file)
      link.setAttribute('download', filename)
      link.dataset.downloadurl = ['application/octet-stream', link.download, link.href].join(':')
      link.click()
    })
    .catch(() => {
      this.$generalNotification('Failed to download file.', 'error')
    })
}

Vue.prototype.$generalNotification = function (message, type = null, timeout = 5000, html = false, actionOptions = {}) {
  if (!message || window._.isEmpty(message.trim())) {
    return
  }

  const uuid = window._.get(actionOptions, 'uuid', null)
  const filename = window._.get(actionOptions, 'filename', null)
  const colorClass = { data: '' }
  let actions = [{
    icon: 'close',
    color: 'black',
    class: 'close-button'
  }]

  switch (type) {
    case 'updated':
      colorClass.data = 'bg-blue-10'
      break
    case 'warning':
      colorClass.data = 'bg-orange-2'
      break
    case 'deleted':
    case 'error':
      colorClass.data = 'bg-red-10'
      break
    case 'export-csv':
      actions.unshift({
        label: 'Download',
        color: 'primary',
        class: 'download-button px-2',
        handler: () => {
          if (uuid) {
            this.$downloadFileWithUuid(uuid, filename, 'csv')
          }
        }
      })
      colorClass.data = 'bg-green-10'
      break
    case 'redirect':
      actions = [{
        label: 'Go to page',
        color: 'primary',
        class: 'px-2',
        handler: () => {
          if (actionOptions.path) {
            this.$router.push({
              path: actionOptions.path
            })
          }
        }
      }]
      colorClass.data = 'bg-green-10'
      break
    case 'error-redirect':
      actions = [{
        label: 'Go to page',
        color: 'primary',
        class: 'px-2',
        handler: () => {
          if (actionOptions.path) {
            this.$router.push({
              path: actionOptions.path
            })
          }
        }
      }]
      colorClass.data = 'bg-red-10'
      break
    default:
      colorClass.data = 'bg-green-10'
  }

  const options = {
    group: false,
    classes: `general-notification text-black ${colorClass.data} ml-7`,
    timeout: timeout,
    message: message,
    position: 'bottom-left',
    html: html,
    actions: actions
  }

  return this.$q.notify(options)
}

window.actionNotificationUnqueuedIntervals = {}

const showActionNotification = function (notificationData) {
  const settings = {
    title: window._.get(notificationData, 'title', null),
    message: window._.get(notificationData, 'message', null),
    messageIcon: window._.get(notificationData, 'messageIcon', null),
    attachment: window._.get(notificationData, 'attachment', null),
    type: window._.get(notificationData, 'type', null),
    contactId: window._.get(notificationData, 'contactId', null),
    communicationId: window._.get(notificationData, 'communicationId', null),
    campaignId: window._.get(notificationData, 'campaignId', null),
    campaignName: window._.get(notificationData, 'campaignName', null),
    ringGroupId: window._.get(notificationData, 'ringGroupId', null),
    ringGroupName: window._.get(notificationData, 'ringGroupName', null),
    phoneNumber: window._.get(notificationData, 'phoneNumber', null),
    dateTime: window._.get(notificationData, 'dateTime', this.$moment()),
    communication: window._.get(notificationData, 'communication', null),
    contact: window._.get(notificationData, 'contact', null)
  }

  // skip if same notification; for type 'call', also require same message so a voicemail
  // ('Missed Call with Voicemail') can replace a missed-call ('Missed Call') for the same
  // communication. incomingCall dedups on communicationId alone (original behavior).
  if (['call', 'incomingCall'].includes(settings.type) &&
    this.$store.state.notifications[settings.type].communicationId === settings.communicationId &&
    (settings.type !== 'call' || this.$store.state.notifications[settings.type].message === settings.message)) {
    return
  }

  const noMessage = !['sms', 'incomingCall'].includes(settings.type) && !settings.message
  const smsNoMessage = settings.type === 'sms' && !settings.message && !settings.attachment

  if (!settings.title || noMessage || smsNoMessage) {
    return
  }

  const data = {
    type: settings.type,
    data: settings
  }

  this.$bvToast.hide(settings.type)

  let unqueuedCounter = 0

  clearInterval(window.actionNotificationUnqueuedIntervals?.[settings.type])

  // only show once no notification of this type is currently in the DOM
  const showUnqueuedNotification = () => {
    if (document.getElementById(settings.type)) {
      return false
    }

    this.$store.commit('SET_NOTIFICATIONS', data)
    this.$bvToast.show(settings.type)
    return true
  }

  // try synchronously first so the common case (no toast present) shows
  // without waiting a full interval tick; only fall back to polling when a
  // same-type toast is still fading out
  if (showUnqueuedNotification()) {
    return
  }

  window.actionNotificationUnqueuedIntervals[settings.type] = setInterval(() => {
    if (showUnqueuedNotification()) {
      clearInterval(window.actionNotificationUnqueuedIntervals?.[settings.type])
    }

    unqueuedCounter++

    if (unqueuedCounter > NOTIFICATION_CONFIGURATION.clearIntervalSecondsLimit) {
      clearInterval(window.actionNotificationUnqueuedIntervals?.[settings.type])
    }
  }, NOTIFICATION_CONFIGURATION.notificationIntervalMilliseconds)
}

const debouncedShowActionNotification = window._.debounce(showActionNotification, 100)

Vue.prototype.$drainReminderNotifications = function () {
  drainReminderNotificationQueue(this.$store, payload => showActionNotification.call(this, payload))
}

// Reminders bypass the shared debounce and go through a dedicated queue:
// they are the only type that can fire twice in the same instant, and both
// the debounce and the single per-type slot would drop all but the last one.
// The queue drains one at a time — here and from the toast's hidden hook.
Vue.prototype.$actionNotification = function (notificationData) {
  if (window._.get(notificationData, 'type', null) === 'reminder') {
    enqueueReminderNotification(this.$store, notificationData)
    this.$drainReminderNotifications()
    return
  }

  return debouncedShowActionNotification.call(this, notificationData)
}

Vue.prototype.$closeActionNotification = function (type) {
  if (document.getElementById(type)) {
    this.$bvToast.hide(type)
  }
}

Vue.prototype.$generalActionNotification = window._.debounce(function (title = 'System Updates', message = 'Refresh your screen', messageIcon = null, type = 'system', contactId = null, communicationId = null, dateTime = this.$moment()) {
  // action notifications that will show up many times
  const icon = (type === 'call' || type === 'call-voicemail') ? 'call-icon' : 'sms-icon'
  // Use a shorter name for this.$createElement
  const h = this.$createElement
  // Create the message
  const vNodesMsg = h(
    'div',
    { class: ['d-flex', 'flex-row', 'align-items-center'] },
    [
      h(
        'div',
        { class: ['mr-2'] },
        [
          h(icon)
        ]
      ),
      h(
        'div',
        { class: ['w-100'] },
        [
          h(
            'div',
            { class: ['d-flex', 'flex-grow-1', 'align-items-baseline'] },
            [
              h('strong', { class: ['mr-auto', 'text-white', 'title'] }, title),
              h('small', { class: ['mr-2', 'text-grey-81', 'time'] }, `${this.$options.filters.shortDateTimePassed(dateTime, false)}`)
            ]
          ),
          h(
            'div',
            { class: ['text-grey-81', 'pt-1', 'message-body'] },
            [
              messageIcon ? h(messageIcon, { class: ['pr-1', 'message-icon'] }) : '',
              message
            ]
          )
        ]
      )
    ]
  )

  // Pass the VNodes as an array for message and title
  this.$bvToast.toast([vNodesMsg], {
    title: null,
    solid: true,
    toastClass: ['action-notification', 'notification-border-round', 'p-3', 'bg-grey-80'],
    bodyClass: ['p-0'],
    headerClass: ['bg-grey-80', 'border-0', 'p-0'],
    toaster: 'b-toaster-bottom-right',
    appendToast: true,
    autoHideDelay: '30000',
    isStatus: true
  })
}, NOTIFICATION_CONFIGURATION.notificationIntervalMilliseconds)

Vue.prototype.$jsonClone = (value) => {
  return cloneDeep(value)
}

Vue.prototype.$copyToClipboard = (value) => {
  if (!value) {
    return
  }

  const el = document.createElement('textarea')
  el.value = value
  el.setAttribute('readonly', '')
  el.style.position = 'absolute'
  el.style.left = '-9999px'
  document.body.appendChild(el)

  const selected = document.getSelection().rangeCount > 0 ? document.getSelection().getRangeAt(0) : false

  el.select()
  document.execCommand('copy')
  document.body.removeChild(el)

  if (selected) {
    document.getSelection().removeAllRanges()
    document.getSelection().addRange(selected)
  }
}

// eslint-disable-next-line no-extend-native
String.prototype.capitalize = function () {
  return this.charAt(0).toUpperCase() + this.slice(1)
}

Vue.prototype.$alphabeticalSort = (items, property = 'name') => {
  if (window._.isEmpty(items)) {
    return items
  }

  return window._.clone(items).sort((a, b) => {
    const textA = a[property].toUpperCase()
    const textB = b[property].toUpperCase()

    return (textA < textB) ? -1 : (textA > textB) ? 1 : 0
  })
}

Vue.prototype.$isNumeric = (value) => {
  let regex = /^-{0,1}\d*\.{0,1}\d+$/

  return regex.test(value)
}

// Sentry identity tags. Registered here so widget routes, which never mount MainLayout,
// are tagged too.
export default ({ store }) => {
  store.subscribe(createSentryIdentitySubscriber(Sentry))
}
